import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { ThemeProvider } from "./contexts/ThemeContext";
import Login from "./pages/Login";

// Lazy load heavy components
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const RequestRedirect = lazy(() => import("./pages/RequestRedirect"));
const Budgets = lazy(() => import("./pages/Budgets"));
const DivisionExpenses = lazy(() => import("./pages/DivisionExpenses"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set axios defaults
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for token in URL (from OIDC callback)
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');

    if (tokenFromUrl) {
      localStorage.setItem('token', tokenFromUrl);
      window.history.replaceState({}, document.title, '/');
      checkAuth();
    } else {
      checkAuth();
    }
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-lg text-gray-600">Загрузка...</div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <Suspense fallback={
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
              <div className="text-lg text-gray-600">Загрузка...</div>
            </div>
          }>
            <Routes>
              <Route
                path="/login"
                element={!user ? <Login setUser={setUser} /> : <Navigate to="/" />}
              />
              <Route
                path="/"
                element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
              />
              <Route
                path="/admin"
                element={
                  user && user.role === "admin" ? (
                    <AdminPanel user={user} onLogout={handleLogout} />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/budgets"
                element={
                  user && (user.role === "admin" || user.role === "manager_org") ? (
                    <Budgets user={user} onLogout={handleLogout} />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/division-expenses"
                element={
                  user && ["admin", "manager_org", "division_manager"].includes(user.role) ? (
                    <DivisionExpenses user={user} onLogout={handleLogout} />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/:requestNumber"
                element={<RequestRedirect />}
              />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
